import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import { CeilingHeightMeasurementType, ListingSearchFilterTypes } from '@root/types';
import { RadioInput, Input } from '@components/shared';
import actions from '@store/actions/listingSearchPage';
import s from '../Filters.module.less';

export type CeilingHeight = {
  type: CeilingHeightMeasurementType;
  magnitude: number | null;
};
type Props = {
  criteria: ListingSearchCriteria;
  onChange: (criteria: () => ListingSearchCriteria) => void;
  type: ListingSearchFilterTypes;
};

const buildAnalyticStringValue = ({ type, magnitude }: CeilingHeight): string =>
  magnitude !== null ? `${type}-${magnitude}` : `${type}-unset-height`;
const CeilingHeightFilter = ({ criteria, onChange, type }: Props) => {
  const { t } = useTranslation('filters');
  const dispatch = useDispatch();

  const [selectedCeilingHeight, setSelectedCeilingHeight] = useState<CeilingHeight>(
    criteria.ceilingHeightFilter(),
  );
  useEffect(() => {
    setSelectedCeilingHeight(criteria.ceilingHeightFilter());
  }, [criteria]);

  const onCeilingHeightOptionChange = (ceilingHeightFilterType: CeilingHeightMeasurementType) => {
    const newCeilingHeight: CeilingHeight = {
      ...selectedCeilingHeight,
      type: ceilingHeightFilterType,
    };

    setSelectedCeilingHeight(newCeilingHeight);
    criteria.changeCeilingHeight(newCeilingHeight);
    if (selectedCeilingHeight.magnitude) {
      onChange(() => {
        dispatch(actions.ceilingHeightFilterChanged(buildAnalyticStringValue(newCeilingHeight)));
        return criteria;
      });
    }
  };

  const onCeilingHeightMagnitudeChange = (ceilingHeight: string) => {
    const coercedNewMagnitude = ceilingHeight === '' ? null : Number(ceilingHeight);
    const newCeilingHeight: CeilingHeight = {
      ...selectedCeilingHeight,
      magnitude: coercedNewMagnitude,
    };

    setSelectedCeilingHeight(newCeilingHeight);
    criteria.changeCeilingHeight(newCeilingHeight);
    onChange(() => {
      dispatch(actions.ceilingHeightFilterChanged(buildAnalyticStringValue(newCeilingHeight)));
      return criteria;
    });
  };

  return (
    <div>
      <div>{t('ceilingHeight.measureBy')}</div>
      <ul className={s.ceilingHeightList}>
        <li className={classnames()}>
          <RadioInput
            name={`ceiling-height-finished-${type}`}
            value="finished"
            onChange={() => onCeilingHeightOptionChange('finished')}
            labelText={t('ceilingHeight.finished')}
            checked={selectedCeilingHeight.type === 'finished'}
            id={`ceiling-height-finished-${type}`}
          />
        </li>
        <li className={classnames()}>
          <RadioInput
            name={`ceiling-height-finished-${type}`}
            value="slabToSlab"
            onChange={() => onCeilingHeightOptionChange('slabToSlab')}
            labelText={t('ceilingHeight.slabToSlab')}
            checked={selectedCeilingHeight.type === 'slabToSlab'}
            id={`ceiling-height-slab-to-slab-${type}`}
          />
        </li>
      </ul>
      <div className={s.minimumFeetLabel}>
        <Input
          name="ceilingHeightMagnitude"
          type="number"
          labelText={t('ceilingHeight.minFt')}
          onChange={(newMagnitude: string) => onCeilingHeightMagnitudeChange(newMagnitude)}
          value={selectedCeilingHeight.magnitude || ''}
          placeholder={t('ceilingHeight.enterMinCeilingFt')}
        />
      </div>
    </div>
  );
};
export default CeilingHeightFilter;
